<template>
  <!-- Поле ответа тикета филиала (p4, p4b): фото слева, «ОТВЕТИТЬ» справа -->
  <div class="reply">
    <!-- Ответ с цитатой: тёмная плашка с оранжевой полоской над полем -->
    <div v-if="replyTo" class="reply__quote">
      <v-icon size="20" class="reply__quote-icon">
        mdi-reply
      </v-icon>
      <div class="reply__quote-text">
        <div class="reply__quote-author">
          В ответ {{ quoteAuthor }}
        </div>
        <div class="reply__quote-preview">
          {{ quotePreviewText }}
        </div>
      </div>
      <img
        v-if="quoteThumb"
        class="reply__quote-thumb"
        :src="quoteThumb"
        alt=""
      >
      <button
        type="button"
        class="reply__quote-close"
        aria-label="Не отвечать на сообщение"
        @click="$emit('cancel-reply')"
      >
        <v-icon size="20">
          mdi-close
        </v-icon>
      </button>
    </div>

    <div v-if="photos.length" class="reply__photos">
      <div v-for="photo in photos" :key="photo.key" class="reply__photo">
        <img v-if="photo.preview" :src="photo.preview" alt="">
        <v-progress-circular
          v-if="photo.uploading"
          indeterminate
          size="20"
          width="2"
          class="reply__photo-progress"
        />
        <button
          type="button"
          class="reply__photo-remove"
          aria-label="Убрать фото"
          @click="removePhoto(photo)"
        >
          <v-icon size="14">
            mdi-close
          </v-icon>
        </button>
      </div>
    </div>

    <div class="reply__row">
      <div class="reply__field" :class="{ 'reply__field--active': focused || text }">
        <button
          type="button"
          class="reply__attach"
          aria-label="Приложить фото"
          :disabled="photos.length >= maxPhotos"
          @click="$refs.file.click()"
        >
          <v-icon size="20">
            mdi-image-plus
          </v-icon>
        </button>
        <input
          ref="file"
          type="file"
          accept="image/jpeg,image/png,image/heic,.heic"
          multiple
          hidden
          @change="addPhotos"
        >
        <textarea
          ref="input"
          v-model="text"
          class="reply__input"
          rows="1"
          :maxlength="limit"
          :placeholder="placeholder"
          @focus="focused = true"
          @blur="focused = false"
          @input="grow"
          @keydown.enter.exact.prevent="submit"
        />
        <span v-if="text.length > limit - 100" class="reply__counter">{{ text.length }} / {{ limit }}</span>
      </div>
      <v-btn
        depressed
        height="52"
        class="reply__send"
        :loading="sending"
        :disabled="uploading"
        @click="submit"
      >
        ОТВЕТИТЬ
        <!-- Иконка справа: контурная на пустом поле, залитая с текстом; ширина не меняется -->
        <v-icon right size="24">
          {{ hasContent ? 'mdi-send' : 'mdi-send-outline' }}
        </v-icon>
      </v-btn>
    </div>
  </div>
</template>

<script>
  import supportApi, { supportErrorText } from '@/api/support'
  import { quotePreview } from '../feed'

  // 52 на одну строку, по 20 на каждую следующую, не выше 20 строк - дальше прокрутка (p4).
  const MAX_HEIGHT = 430
  const MAX_PHOTOS = 10
  const MAX_PHOTO_BYTES = 10 * 1024 * 1024

  export default {
    name: 'ReplyField',
    props: {
      placeholder: { type: String, default: '' },
      replyTo: { type: Object, default: null },
      sending: { type: Boolean, default: false },
    },
    data () {
      return {
        text: '',
        photos: [],
        focused: false,
        limit: 1000,
        maxPhotos: MAX_PHOTOS,
      }
    },
    computed: {
      uploading () {
        return this.photos.some(p => p.uploading)
      },
      hasContent () {
        return Boolean(this.text.trim()) || this.photos.some(p => p.id)
      },
      quoteAuthor () {
        const author = this.replyTo?.author?.name || ''

        return this.replyTo?.from === 'branch' ? author : `${author} · поддержка FAKT`
      },
      quotePreviewText () {
        return quotePreview(this.replyTo?.text, 90) || 'Фото'
      },
      quoteThumb () {
        return this.replyTo?.attachments?.[0]?.url || null
      },
    },
    watch: {
      replyTo (value) {
        if (value) this.$nextTick(() => this.$refs.input.focus())
      },
    },
    beforeDestroy () {
      this.photos.forEach(p => p.preview && URL.revokeObjectURL(p.preview))
    },
    methods: {
      grow () {
        const input = this.$refs.input
        input.style.height = 'auto'
        input.style.height = `${Math.min(input.scrollHeight, MAX_HEIGHT)}px`
      },
      async addPhotos (event) {
        const files = Array.from(event.target.files || [])
        event.target.value = ''
        for (const file of files) {
          if (this.photos.length >= MAX_PHOTOS) {
            this.showWarningMessage(`Не больше ${MAX_PHOTOS} фото в одном сообщении`)
            break
          }
          if (file.size > MAX_PHOTO_BYTES) {
            this.showWarningMessage(`«${file.name}» больше 10 МБ`)
            continue
          }
          const photo = { key: `${file.name}-${Date.now()}-${Math.random()}`, id: null, uploading: true, preview: URL.createObjectURL(file) }
          this.photos.push(photo)
          // Загружаем сразу: к нажатию «Ответить» id уже готовы.
          try {
            const uploaded = await supportApi.upload(file)
            photo.id = uploaded.id
          } catch (e) {
            this.showErrorMessage(supportErrorText(e, 'Не удалось загрузить фото'))
            this.removePhoto(photo)
          } finally {
            photo.uploading = false
          }
        }
      },
      removePhoto (photo) {
        if (photo.preview) URL.revokeObjectURL(photo.preview)
        this.photos = this.photos.filter(p => p !== photo)
      },
      submit () {
        if (this.sending || this.uploading) return
        if (!this.hasContent) {
          this.$refs.input.focus()
          return
        }
        this.$emit('submit', {
          text: this.text.trim() || null,
          attachmentIds: this.photos.filter(p => p.id).map(p => p.id),
          replyToId: this.replyTo?.id || null,
        })
      },
      // Родитель зовёт после успешной отправки; при ошибке текст и фото остаются.
      reset () {
        this.photos.forEach(p => p.preview && URL.revokeObjectURL(p.preview))
        this.text = ''
        this.photos = []
        this.$nextTick(this.grow)
      },
    },
  }
</script>

<style scoped lang="less">
@import '../tokens.less';

.reply {
  flex-shrink: 0;
}

.reply__quote {
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 8px;
  padding: 8px 12px;
  border-left: 3px solid @fakt-orange;
  border-radius: 4px;
  background: @fakt-dark;
}

.reply__quote-icon,
.reply__quote-close .v-icon {
  color: @fakt-white !important;
}

.reply__quote-text {
  flex: 1;
  min-width: 0;
}

.reply__quote-author {
  font-size: 14px;
  font-weight: 500;
  color: @fakt-orange;
}

.reply__quote-preview {
  overflow: hidden;
  font-size: 13px;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: @fakt-on-dark-muted;
}

.reply__quote-thumb {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  object-fit: cover;
}

.reply__photos {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
}

.reply__photo {
  position: relative;
  width: 56px;
  height: 56px;
  overflow: hidden;
  border-radius: 8px;
  background: @fakt-surface;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.reply__photo-progress {
  position: absolute;
  top: 18px;
  left: 18px;
}

.reply__photo-remove {
  position: absolute;
  top: 4px;
  right: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(0, 0, 0, .65);

  .v-icon {
    color: @fakt-white !important;
  }
}

.reply__row {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.reply__field {
  display: flex;
  flex: 1;
  gap: 10px;
  align-items: flex-end;
  min-height: 52px;
  padding: 7px 14px 7px 8px;
  border: 1px solid @fakt-card-border;
  border-radius: 10px;
  background: @fakt-white;
  transition: border-color 0.16s ease;

  &--active {
    border-color: @fakt-orange;
  }
}

.reply__attach {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: @fakt-surface;

  .v-icon {
    color: @fakt-orange !important;
  }

  &:disabled {
    opacity: .4;
  }
}

.reply__input {
  flex: 1;
  min-height: 20px;
  max-height: 430px;
  margin-bottom: 8px;
  overflow-y: auto;
  font-size: 14px;
  line-height: 20px;
  resize: none;
  outline: none;
  color: @fakt-text;

  &::placeholder {
    color: @fakt-text-3;
  }
}

.reply__counter {
  flex-shrink: 0;
  margin-bottom: 8px;
  font-size: 12px;
  color: @fakt-text-3;
}

.reply__send {
  min-width: 156px !important;
  border-radius: 26px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.5px;
  color: @fakt-white !important;
  background-color: @fakt-orange !important;
}
</style>
